import React, { useState, useMemo, useEffect, useRef } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Caustics } from "@/components/SwimmingFX";
import { Search, X, Users } from "lucide-react";
import api from "@/lib/api";

const STROKE_IMG = {
  freestyle:    "https://images.pexels.com/photos/863988/pexels-photo-863988.jpeg?auto=compress&cs=tinysrgb&w=400",
  backstroke:   "https://images.unsplash.com/photo-1530549387789-4c1017266635?w=400&q=80",
  breaststroke: "https://images.pexels.com/photos/261185/pexels-photo-261185.jpeg?auto=compress&cs=tinysrgb&w=400",
  butterfly:    "https://images.pexels.com/photos/73760/swimming-swimmer-female-race-73760.jpeg?auto=compress&cs=tinysrgb&w=400",
  medley:       "https://images.unsplash.com/photo-1519315901367-f34ff9154487?w=400&q=80",
  relay:        "https://images.pexels.com/photos/863988/pexels-photo-863988.jpeg?auto=compress&cs=tinysrgb&w=400",
};

const STROKE_COLOR = {
  freestyle: "#00F0FF", backstroke: "#7DD3FC", breaststroke: "#34D399",
  butterfly: "#FBBF24", medley: "#F472B6", relay: "#A78BFA",
};

function getStroke(eventName = "") {
  const n = eventName.toLowerCase();
  if (n.includes("relay"))        return "relay";
  if (n.includes("medley") || n.includes(" im")) return "medley";
  if (n.includes("butterfly") || n.includes("butter fly")) return "butterfly";
  if (n.includes("breaststroke") || n.includes("breast stroke")) return "breaststroke";
  if (n.includes("backstroke") || n.includes("back stroke"))   return "backstroke";
  return "freestyle";
}

function getAgeCategory(eventName = "") {
  const m = eventName.match(/(\d+)\s+Year\s+Olds/i);
  if (m) return `${m[1]} Year Olds`;
  // Relays are grouped by combined age, e.g. "Mixed 4x50m Freestyle Relay 160-199"
  const r = /relay/i.test(eventName) && eventName.match(/\b(\d{2,3})\s*-\s*(\d{2,3})\b/);
  return r ? `${r[1]}-${r[2]} (Relay)` : null;
}

/* Same days as the Results page */
const DAYS = [
  { day: 1, label: "Day 1", date: "2026-10-04" },
  { day: 2, label: "Day 2", date: "2026-10-05" },
  { day: 3, label: "Day 3", date: "2026-10-06" },
  { day: 4, label: "Day 4", date: "2026-10-07" },
  { day: 5, label: "Day 5", date: "2026-10-08" },
  { day: 6, label: "Day 6", date: "2026-10-09" },
  { day: 7, label: "Day 7", date: "2026-10-10" },
];

const STROKE_LABELS = {
  freestyle: "Freestyle", backstroke: "Backstroke", breaststroke: "Breaststroke",
  butterfly: "Butterfly", medley: "Medley / IM", relay: "Relay",
};

export default function HeatList() {
  const [selectedEvent,     setSelectedEvent]     = useState(null);
  const [strokeFilter,      setStrokeFilter]      = useState("all");
  const [ageCatFilter,      setAgeCatFilter]      = useState("all");
  const [genderFilter,      setGenderFilter]      = useState("all");
  const [search,            setSearch]            = useState("");
  const [allHeatLists,      setAllHeatLists]      = useState([]);
  const [selectedDay,       setSelectedDay]       = useState(null);   // 1..8, or "none" for lists with no day yet
  const [loading,           setLoading]           = useState(false);
  const tabRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();

  /* Fetch all heatlists */
  useEffect(() => {
    const fetchHeatLists = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/heatlists`);
        setAllHeatLists(data);
      } catch (err) {
        console.error("Failed to fetch heat lists", err);
        setAllHeatLists([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHeatLists();
  }, []);

  /* Days that have heat lists; lists published without a day show under "Unassigned" */
  const daysWithLists = useMemo(() => new Set(allHeatLists.map(r => r.day).filter(Boolean)), [allHeatLists]);
  const hasUnassigned = useMemo(() => allHeatLists.some(r => !r.day), [allHeatLists]);

  /* Start on the first day that has heat lists */
  useEffect(() => {
    setSelectedDay(prev => {
      if (prev !== null && (prev === "none" ? hasUnassigned : daysWithLists.has(prev))) return prev;
      const first = DAYS.find(d => daysWithLists.has(d.day));
      return first ? first.day : (hasUnassigned ? "none" : prev);
    });
  }, [daysWithLists, hasUnassigned]);

  const heatLists = useMemo(
    () => allHeatLists.filter(r => (selectedDay === "none" ? !r.day : r.day === selectedDay)),
    [allHeatLists, selectedDay]
  );

  /* All event names */
  const eventNames = useMemo(() => {
    const uniqueEvents = [...new Set(heatLists.map(r => r.event_name))];
    // Sort events by event_number if available
    const eventNumbers = {};
    heatLists.forEach(h => {
      if (!eventNumbers[h.event_name]) {
        eventNumbers[h.event_name] = h.event_number;
      }
    });
    return uniqueEvents.sort((a, b) => (eventNumbers[a] || 0) - (eventNumbers[b] || 0));
  }, [heatLists]);

  /* Available filter options */
  const availableAgeCategories = useMemo(() =>
    [...new Set(eventNames.map(getAgeCategory).filter(Boolean))].sort(
      (a, b) => parseInt(a) - parseInt(b)
    ), [eventNames]);

  const availableGenders = useMemo(() =>
    [...new Set(heatLists.map(r => r.gender).filter(Boolean))].sort(), [heatLists]);

  /* Events after stroke / age / gender filters */
  const filteredEvents = useMemo(() =>
    eventNames.filter(evName => {
      if (strokeFilter  !== "all" && getStroke(evName) !== strokeFilter)      return false;
      if (ageCatFilter  !== "all" && getAgeCategory(evName) !== ageCatFilter) return false;
      if (genderFilter  !== "all") {
        const g = heatLists.find(r => r.event_name === evName)?.gender || "";
        if (g.toLowerCase() !== genderFilter.toLowerCase()) return false;
      }
      return true;
    }), [eventNames, strokeFilter, ageCatFilter, genderFilter, heatLists]);

  /* Auto-select first visible event */
  useEffect(() => {
    setSelectedEvent(prev =>
      filteredEvents.includes(prev) ? prev : (filteredEvents[0] || null)
    );
  }, [filteredEvents]);

  /* Swimmers for selected event */
  const eventSwimmers = useMemo(() => {
    if (!selectedEvent) return [];
    const q = search.toLowerCase();
    return heatLists
      .filter(r => r.event_name === selectedEvent)
      .filter(r => !q
        || r.athlete.toLowerCase().includes(q)
        || (r.team || "").toLowerCase().includes(q)
        || (r.relay_swimmers || []).some(s => (s.name || "").toLowerCase().includes(q)));
  }, [heatLists, selectedEvent, search]);

  /* Group swimmers by heat */
  const heats = useMemo(() => {
    const grouped = {};
    eventSwimmers.forEach(s => {
      if (!grouped[s.heat]) grouped[s.heat] = [];
      grouped[s.heat].push(s);
    });
    return Object.keys(grouped).sort((a, b) => parseInt(a) - parseInt(b)).map(heat => ({
      heat: parseInt(heat),
      swimmers: grouped[heat].sort((a, b) => a.lane - b.lane)
    }));
  }, [eventSwimmers]);

  const hasEventFilters = strokeFilter !== "all" || ageCatFilter !== "all" || genderFilter !== "all";
  const hasFilters      = hasEventFilters || search;
  const clearEventFilters = () => { setStrokeFilter("all"); setAgeCatFilter("all"); setGenderFilter("all"); };

  return (
    <div className="min-h-dvh bg-[#0B1429] text-white">
      <Navbar />

      {/* ── Hero ── */}
      <div className="relative pt-32 pb-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F2040] via-[#0B1429] to-[#0B1429]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-56 bg-[#00F0FF]/[0.06] blur-[90px] rounded-full pointer-events-none" />
        {!shouldReduceMotion && <Caustics opacity={0.07} />}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#00F0FF] mb-3">
              AMSC India 2026 · Official Programme
            </p>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h1 className="font-display font-black text-4xl md:text-5xl">
                Competition <span className="text-[#00F0FF]">Heat Lists</span>
              </h1>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">

        {/* ── Day tabs ── */}
        {allHeatLists.length > 0 && (
          <div className="mb-8 flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" role="tablist" aria-label="Day">
            {DAYS.map(d => {
              const has = daysWithLists.has(d.day);
              const isSelected = selectedDay === d.day;
              return (
                <button
                  key={d.day}
                  role="tab"
                  aria-selected={isSelected}
                  disabled={!has}
                  onClick={() => setSelectedDay(d.day)}
                  className={[
                    "relative flex flex-col items-center shrink-0 min-w-[72px] px-4 py-4 rounded-xl border font-display font-bold text-sm transition-all duration-200 select-none touch-manipulation",
                    isSelected
                      ? "bg-[#00F0FF]/10 border-[#00F0FF]/60 text-[#00F0FF] shadow-[0_0_20px_rgba(0,240,255,0.15)] cursor-pointer"
                      : has
                      ? "bg-[#142040]/60 border-white/[0.07] text-[#94A3B8] hover:border-white/[0.18] hover:text-white cursor-pointer"
                      : "bg-[#0F1830]/40 border-white/[0.04] text-white/20 cursor-default",
                  ].join(" ")}
                >
                  <span className="text-[10px] tracking-[0.18em] uppercase font-semibold opacity-70 mb-0.5 tabular-nums">{d.date.slice(5).replace("-", "/")}</span>
                  <span className="leading-none">{d.label}</span>
                  {!has && <span className="text-[9px] mt-0.5 opacity-40 font-normal">TBD</span>}
                </button>
              );
            })}
            {hasUnassigned && (
              <button
                role="tab"
                aria-selected={selectedDay === "none"}
                onClick={() => setSelectedDay("none")}
                className={`shrink-0 min-w-[72px] px-4 py-4 rounded-xl border font-display font-bold text-sm cursor-pointer touch-manipulation ${selectedDay === "none" ? "bg-[#FBBF24]/10 border-[#FBBF24]/60 text-[#FBBF24]" : "bg-[#142040]/60 border-white/[0.07] text-[#94A3B8] hover:text-white"}`}
              >
                Unassigned
              </button>
            )}
          </div>
        )}

        {/* ── Event image cards ── */}
        {eventNames.length > 0 ? (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.15 }}
              className="mb-6"
            >
              {/* ── Event filter bar ── */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#64748B]">
                  Events
                </span>
                <div className="flex flex-wrap gap-2 ml-auto">
                  {availableGenders.length > 1 && (
                    <FilterPill value={genderFilter} onChange={setGenderFilter} options={[
                      { value: "all", label: "All Genders" },
                      ...availableGenders.map(g => ({ value: g, label: g })),
                    ]} />
                  )}
                  <FilterPill value={strokeFilter} onChange={setStrokeFilter} options={[
                    { value: "all", label: "All Strokes" },
                    ...Object.entries(STROKE_LABELS)
                      .filter(([key]) => eventNames.some(e => getStroke(e) === key))
                      .map(([key, label]) => ({ value: key, label })),
                  ]} />
                  {availableAgeCategories.length > 0 && (
                    <FilterPill value={ageCatFilter} onChange={setAgeCatFilter} options={[
                      { value: "all", label: "All Ages" },
                      ...availableAgeCategories.map(a => ({ value: a, label: a })),
                    ]} />
                  )}
                  {hasEventFilters && (
                    <button onClick={clearEventFilters} className="h-8 px-3 rounded-full border border-white/[0.08] text-xs text-[#64748B] hover:text-white hover:border-white/20 transition-colors cursor-pointer flex items-center gap-1">
                      <X className="w-3 h-3" /> Clear
                    </button>
                  )}
                </div>
              </div>

              <div
                ref={tabRef}
                className="flex gap-3 overflow-x-auto pb-2 px-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              >
                {filteredEvents.length === 0 ? (
                  <p className="text-sm text-[#64748B] py-4">No events match the selected filters.</p>
                ) : filteredEvents.map(eventName => {
                  const stroke = getStroke(eventName);
                  const img    = STROKE_IMG[stroke];
                  const color  = STROKE_COLOR[stroke];
                  const active = selectedEvent === eventName;

                  return (
                    <button
                      key={eventName}
                      onClick={() => setSelectedEvent(eventName)}
                      className={[
                        "relative shrink-0 w-44 h-28 rounded-2xl overflow-hidden border-2 transition-all duration-250 cursor-pointer touch-manipulation group",
                        active
                          ? "border-[#00F0FF]/70 scale-[1.02]"
                          : "border-white/[0.06] hover:border-white/25 hover:scale-[1.01]",
                      ].join(" ")}
                      style={active ? { boxShadow: `0 0 24px rgba(0,240,255,0.25)` } : {}}
                    >
                      <img
                        src={img}
                        alt={eventName}
                        loading="lazy"
                        className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${active ? "scale-110 brightness-75" : "scale-100 brightness-50 group-hover:brightness-60 group-hover:scale-105"}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1429]/90 via-[#0B1429]/30 to-transparent" />
                      {active && (
                        <motion.div
                          layoutId="event-active-line"
                          className="absolute bottom-0 left-0 right-0 h-0.5"
                          style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
                        />
                      )}
                      <div
                        className="absolute top-3 right-3 w-2 h-2 rounded-full"
                        style={{ background: color, boxShadow: `0 0 8px ${color}` }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-white font-bold text-xs leading-tight line-clamp-2">{eventName}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>

            {/* ── Search within selected event ── */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative w-full sm:w-52 sm:ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" strokeWidth={1.5} />
                <input
                  type="text"
                  placeholder="Athlete or team…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  aria-label="Search results"
                  className="h-9 w-full bg-white/[0.04] border border-white/[0.07] rounded-full pl-8 pr-8 text-sm text-white placeholder:text-[#3A4A6A] focus:outline-none focus:border-[#00F0FF]/40 transition-colors"
                />
                {search && (
                  <button onClick={() => setSearch("")} aria-label="Clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors cursor-pointer">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* ── Heats ── */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedEvent}
                initial={shouldReduceMotion ? {} : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="space-y-8"
              >
                {heats.length > 0 ? (
                  <>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#64748B] mb-2">
                      {selectedEvent}
                    </p>
                    
                    {heats.map((h, idx) => (
                      <motion.div
                        key={h.heat}
                        initial={shouldReduceMotion ? {} : { opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + idx * 0.05, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="rounded-2xl bg-[#142040]/40 backdrop-blur-sm border border-white/[0.08] overflow-hidden"
                      >
                        <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00F0FF]">
                            Heat {h.heat}
                          </p>
                          {h.swimmers[0]?.is_relay && (
                            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#A78BFA]">
                              {h.swimmers.length} teams
                            </span>
                          )}
                        </div>
                        {h.swimmers[0]?.is_relay ? (
                          <RelayHeatTable rows={h.swimmers} query={search} />
                        ) : (
                        <table className="w-full table-fixed">
                          <thead>
                            <tr className="border-b border-white/[0.05]">
                              <th className="px-3 sm:px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B] w-14 sm:w-20">Lane</th>
                              <th className="px-3 sm:px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B]">Athlete</th>
                              <th className="hidden sm:table-cell px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B] w-20 sm:w-24">Age</th>
                              <th className="hidden md:table-cell px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B] w-32 lg:w-48">Team</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/[0.04]">
                            {h.swimmers.map((r, i) => (
                              <tr
                                key={`${r.athlete}-${i}`}
                                className="hover:bg-white/[0.02] transition-colors duration-150"
                              >
                                <td className="px-3 sm:px-5 py-3 text-sm text-[#64748B] tabular-nums">{r.lane}</td>
                                <td className="px-3 sm:px-5 py-3 text-sm font-medium text-white truncate">
                                  <span className="block truncate">{r.athlete}</span>
                                  <span className="md:hidden text-xs text-[#64748B] truncate block max-w-[180px]">{r.team}</span>
                                </td>
                                <td className="hidden sm:table-cell px-5 py-3 text-sm text-[#94A3B8]">{r.age}</td>
                                <td className="hidden md:table-cell px-5 py-3 text-sm text-[#94A3B8] truncate max-w-[200px]">{r.team}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        )}
                      </motion.div>
                    ))}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-14 h-14 rounded-full bg-[#142040]/60 border border-white/[0.07] flex items-center justify-center mb-4">
                      <Users className="w-6 h-6 text-[#3A4A6A]" strokeWidth={1.5} />
                    </div>
                    <p className="text-sm font-semibold text-[#64748B]">
                      {hasFilters ? "No swimmers match your search" : "No swimmers found"}
                    </p>
                    {hasFilters && (
                      <button onClick={() => { clearEventFilters(); setSearch(""); }} className="mt-3 text-xs text-[#00F0FF] hover:text-white transition-colors cursor-pointer">
                        Clear search
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-full bg-[#142040]/60 border border-white/[0.07] flex items-center justify-center mb-4">
              <Users className="w-7 h-7 text-[#3A4A6A]" strokeWidth={1.5} />
            </div>
            <p className="text-base font-semibold text-[#64748B]">
              {loading ? "Loading heat lists…" : "Heat lists have not been published yet"}
            </p>
          </motion.div>
        )}
      </div>

      <Footer />
    </div>
  );
}

const RelayHeatTable = ({ rows, query = "" }) => {
  const q = query.trim().toLowerCase();
  return (
    <div className="divide-y divide-white/[0.04]">
      {rows.map((r, i) => (
        <div key={`${r.athlete}-${i}`} className="grid grid-cols-[48px_1fr] sm:grid-cols-[72px_1fr_auto] gap-x-3 px-3 sm:px-5 py-4 hover:bg-white/[0.02] transition-colors duration-150">
          <div className="flex flex-col items-start">
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#64748B]">Lane</span>
            <span className="font-display font-black text-2xl text-[#A78BFA] tabular-nums leading-none mt-0.5">{r.lane}</span>
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-sm font-bold text-white">{r.athlete}</span>
              {r.team && <span className="text-xs text-[#64748B]">{r.team}</span>}
            </div>
            <ol className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1">
              {(r.relay_swimmers || []).map(s => {
                const hit = q && (s.name || "").toLowerCase().includes(q);
                return (
                  <li key={s.order} className="flex items-center gap-2 text-xs min-w-0">
                    <span className="w-4 h-4 shrink-0 rounded-full bg-[#A78BFA]/15 text-[#A78BFA] text-[9px] font-bold flex items-center justify-center tabular-nums">{s.order}</span>
                    <span className={`truncate ${hit ? "text-[#00F0FF] font-semibold" : "text-[#CBD5E1]"}`}>{s.name}</span>
                    {s.stroke && <span className="text-[10px] text-[#64748B] shrink-0">{s.stroke}</span>}
                    {s.age && <span className="ml-auto text-[11px] text-[#64748B] tabular-nums shrink-0">{s.age}</span>}
                  </li>
                );
              })}
            </ol>
          </div>
          {r.total_age && (
            <div className="col-start-2 sm:col-start-auto mt-2 sm:mt-0 sm:text-right">
              <span className="text-[10px] uppercase tracking-[0.14em] text-[#64748B] block">Total Age</span>
              <span className="text-sm font-semibold text-white tabular-nums">{r.total_age}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const FilterPill = ({ value, onChange, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={[
        "h-8 border rounded-full pl-3 pr-7 text-xs appearance-none cursor-pointer transition-colors focus:outline-none",
        value !== "all"
          ? "bg-[#00F0FF]/10 border-[#00F0FF]/40 text-[#00F0FF]"
          : "bg-white/[0.04] border-white/[0.08] text-[#94A3B8] hover:border-white/20 hover:text-white",
      ].join(" ")}
    >
      {options.map(o => <option key={o.value} value={o.value} className="bg-[#0B1429] text-white">{o.label}</option>)}
    </select>
    <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-[#64748B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);
